import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { Box, Drawer, Stack } from '@mui/material';
// hooks
import { ErrorScreen } from 'components/loading-screen';
import { LoadingFullScreen } from 'components/loading-screen/LoadingScreen';
import useResponsive from '../../../hooks/useResponsive';
// config
import { NAV } from '../../../config-global';
// components
import Logo from '../../../components/logo';
import { NavSectionVertical } from '../../../components/nav-section';
import Scrollbar from '../../../components/scrollbar';
import { getUltimateNavigation } from '../../../utils/helper';
//
import { useGetNavigationConfig } from '../../../services/auth/authServices';
import NavAccount from './NavAccount';
import NavSidebarLogout from './NavSidebarLogout';
import NavSidebarPromo from './NavSidebarPromo';
import NavToggleButton from './NavToggleButton';

// ----------------------------------------------------------------------

NavVertical.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

export default function NavVertical({ openNav, onCloseNav }) {
  const { pathname } = useLocation();
  const { data: navConfig, isLoading, isError } = useGetNavigationConfig();

  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  if (isLoading) return <LoadingFullScreen />;
  if (isError) return <ErrorScreen />;

  const ultimateNavigation = getUltimateNavigation(navConfig);

  const paperSx = {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    height: '100%',
    bgcolor: '#fff',
    borderRight: 'none',
    boxShadow: '2px 0 10px -4px rgba(15, 23, 42, 0.08)',
  };

  const renderContent = (
    <Box sx={{ height: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
      <Scrollbar
        sx={{
          height: 1,
          '& .simplebar-content': {
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Stack
          spacing={2}
          sx={{
            pt: 3,
            pb: 2,
            px: 2.5,
            flexShrink: 0,
            alignItems: 'center',
            borderBottom: '1px dashed #e5e7eb',
          }}
        >
          <Logo />

          <NavAccount />
        </Stack>

        <NavSectionVertical data={ultimateNavigation} />

        <Box sx={{ flexGrow: 1 }} />

        <NavSidebarPromo />
      </Scrollbar>
      </Box>

      <Box sx={{ borderTop: '1px dashed #e5e7eb', flexShrink: 0 }} />

      <NavSidebarLogout onClose={onCloseNav} />
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_DASHBOARD },
      }}
    >
      <NavToggleButton />

      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            className: 'app-admin-sidebar',
            sx: {
              zIndex: 0,
              width: NAV.W_DASHBOARD,
              ...paperSx,
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            className: 'app-admin-sidebar',
            sx: {
              width: NAV.W_DASHBOARD,
              ...paperSx,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
